import { PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { type FormEvent, useState } from "react";
import { api, q } from "./api.ts";
import { notify } from "./feedback.ts";
import { fullDay, money, monthLabel } from "./format.ts";
import { LEDGER_LABELS as L } from "./labels.ts";
import { BarChart, Button, EmptyState, ErrorText, Field, Kbd, Modal, Section, Stat } from "./ui.tsx";
import { Select } from "./ui/select.tsx";
import { Skeleton } from "./ui/skeleton.tsx";

// Insights › Money (docs/product-decisions.md 2026-10-31; docs/design-system.md
// §0.4k): what running the product costs and earns, as a person records it.
// Totals stay in each currency; a month nobody recorded shows nothing, not zero.

type Direction = C.LedgerEntry["direction"];
type Editing = { entry: C.LedgerEntry | null; direction: Direction };
const CURRENCIES = ["USD", "CNY", "EUR", "GBP", "JPY", "HKD", "SGD", "CAD", "AUD"];
const categoriesOf = (direction: Direction) => Object.entries(L.categories[direction]).map(([value, label]) => ({ value, label }));
const categoryName = (direction: Direction, category: string) => L.categories[direction][category] ?? category;

function EntryForm({ product, ledger, editing, onDone }: { product: C.Product; ledger: C.Ledger; editing: Editing; onDone: () => void }) {
  const client = useQueryClient();
  const initial = editing.entry;
  const campaigns = useQuery(q.productCampaigns(product.id));
  const lastCurrency = ledger.entries[0]?.currency ?? "USD";
  const [direction, setDirection] = useState<Direction>(initial?.direction ?? editing.direction);
  const [category, setCategory] = useState(initial?.category ?? (editing.direction === "cost" ? "hosting" : "subscriptions"));
  const [counterparty, setCounterparty] = useState(initial?.counterparty ?? "");
  const [amount, setAmount] = useState(initial?.amount ?? "");
  const [currency, setCurrency] = useState(initial?.currency ?? lastCurrency);
  const [recurrence, setRecurrence] = useState<C.LedgerEntry["recurrence"]>(initial?.recurrence ?? "monthly");
  const [startsOn, setStartsOn] = useState(initial?.starts_on ?? ledger.today);
  const [endsOn, setEndsOn] = useState(initial?.ends_on ?? "");
  const [note, setNote] = useState(initial?.note ?? "");
  const [campaignId, setCampaignId] = useState(initial?.campaign_id ?? "");
  const changeDirection = (next: Direction) => { setDirection(next); if (!(category in L.categories[next])) setCategory(next === "cost" ? "hosting" : "subscriptions"); };
  const save = useMutation({
    mutationFn: () => {
      const body = { direction, category, counterparty, amount: amount.trim(), currency, recurrence, starts_on: startsOn, ends_on: recurrence === "once" || !endsOn ? null : endsOn, note: note.trim() || null, campaign_id: campaignId || null };
      const base = `/api/products/${encodeURIComponent(product.id)}/ledger/entries`;
      return initial ? api.put<C.LedgerEntry>(`${base}/${encodeURIComponent(initial.id)}`, { ...body, revision: initial.revision }) : api.post<C.LedgerEntry>(base, body);
    },
    onSuccess: async () => { await client.invalidateQueries({ queryKey: ["ledger", product.id] }); notify.success(L.saved); onDone(); },
  });
  const submit = (event: FormEvent) => { event.preventDefault(); save.mutate(); };
  const currencies = CURRENCIES.includes(currency) ? CURRENCIES : [currency, ...CURRENCIES];
  return <form className="z-form z-flow" onSubmit={submit} onKeyDown={(event) => { if ((event.metaKey || event.ctrlKey) && event.key === "Enter") { event.preventDefault(); save.mutate(); } }}>
    <div className="z-grid2">
      <Field label={L.direction}><Select variant="form" label={L.direction} value={direction} onValueChange={(next) => changeDirection(next as Direction)} options={(["cost", "revenue"] as const).map((value) => ({ value, label: L.directions[value] }))} /></Field>
      <Field label={L.category}><Select variant="form" label={L.category} value={category} onValueChange={setCategory} options={categoriesOf(direction)} /></Field>
    </div>
    <Field label={L.counterparty} help={L.counterpartyHelp}><input className="z-input" aria-label={L.counterparty} value={counterparty} onChange={(event) => setCounterparty(event.target.value)} maxLength={120} required autoFocus /></Field>
    <div className="z-grid2">
      <Field label={L.amount}><input className="z-input" aria-label={L.amount} inputMode="decimal" pattern="\d{1,12}(\.\d{1,2})?" value={amount} onChange={(event) => setAmount(event.target.value)} required /></Field>
      <Field label={L.currency}><Select variant="form" label={L.currency} value={currency} onValueChange={setCurrency} options={currencies.map((value) => ({ value, label: value }))} /></Field>
    </div>
    <div className="z-grid2">
      <Field label={L.recurrence}><Select variant="form" label={L.recurrence} value={recurrence} onValueChange={(next) => setRecurrence(next as C.LedgerEntry["recurrence"])} options={(["once", "monthly", "yearly"] as const).map((value) => ({ value, label: L.recurrences[value] }))} /></Field>
      <Field label={recurrence === "once" ? L.startsOn : L.startsOnRecurring}><input className="z-input" aria-label={recurrence === "once" ? L.startsOn : L.startsOnRecurring} type="date" value={startsOn} onChange={(event) => setStartsOn(event.target.value)} required /></Field>
    </div>
    {recurrence !== "once" && <Field label={L.endsOn} help={L.endsOnHelp}><input className="z-input" aria-label={L.endsOn} type="date" min={startsOn} value={endsOn} onChange={(event) => setEndsOn(event.target.value)} /></Field>}
    {(campaigns.data?.length ?? 0) > 0 && <Field label={L.campaign}><Select variant="form" label={L.campaign} value={campaignId} onValueChange={setCampaignId} options={[{ value: "", label: L.noCampaign }, ...(campaigns.data ?? []).map((campaign) => ({ value: campaign.id, label: campaign.title }))]} /></Field>}
    <Field label={L.note}><textarea className="z-textarea" aria-label={L.note} value={note} onChange={(event) => setNote(event.target.value)} maxLength={500} /></Field>
    <ErrorText error={save.error} />
    <div className="z-inline"><Button type="submit" tone="primary" busy={save.isPending}>{L.save} <Kbd>⌘↵</Kbd></Button><Button onClick={onDone} disabled={save.isPending}>{L.cancel}</Button></div>
  </form>;
}

function Summary({ summary }: { summary: C.Ledger["summary"][number] }) {
  const { currency } = summary;
  return <Section title={currency}>
    <div className="z-stats">
      <Stat label={`${L.thisMonth} · ${L.cost}`} value={money(summary.this_month.cost, currency)} />
      <Stat label={`${L.thisMonth} · ${L.revenue}`} value={money(summary.this_month.revenue, currency)} />
      <Stat label={`${L.thisMonth} · ${L.net}`} value={money(summary.this_month.net, currency)} />
      <Stat label={L.runRate} value={money(summary.monthly_run_rate.cost, currency)} of={L.perRecurrence.monthly} note={L.runRateNote} />
    </div>
    <BarChart title={L.months(currency)} format={(value) => money(value, currency)}
      series={[{ key: "cost", label: L.cost, color: "var(--z-icon)" }, { key: "revenue", label: L.revenue, color: "var(--z-accent)" }]}
      data={summary.months.map((month) => ({ label: monthLabel(month.month), values: { cost: month.cost === null ? null : Number(month.cost), revenue: month.revenue === null ? null : Number(month.revenue) } }))} />
    {summary.cost_by_category.length > 0 && <p className="z-small z-muted">{L.byCategory}</p>}
    {summary.cost_by_category.length > 0 && <dl className="z-props">
      {summary.cost_by_category.map((row) => [<dt key={`${row.category}-t`}>{categoryName("cost", row.category)}</dt>, <dd key={`${row.category}-d`}>{money(row.amount, currency)}</dd>])}
    </dl>}
  </Section>;
}

function Entries({ ledger, direction, onEdit, onDelete }: { ledger: C.Ledger; direction: Direction; onEdit: (entry: C.LedgerEntry) => void; onDelete: (entry: C.LedgerEntry) => void }) {
  const rows = ledger.entries.filter((entry) => entry.direction === direction);
  if (!rows.length) return null;
  return <Section title={direction === "cost" ? L.costs : L.revenues}>
    <div className="z-list">{rows.map((entry) => {
      const ended = entry.ends_on !== null && entry.ends_on < ledger.today;
      return <div className="z-row" key={entry.id}>
        <div className="z-row-main">
          <div className="z-row-title">{entry.counterparty}</div>
          <div className="z-row-meta">
            <span>{categoryName(entry.direction, entry.category)}</span>
            <span>{L.recurrences[entry.recurrence]} · {fullDay(entry.starts_on)}{entry.ends_on ? ` – ${fullDay(entry.ends_on)}` : ""}</span>
            {ended && <span>{L.ended}</span>}
          </div>
          {entry.note && <p className="z-row-body">{entry.note}</p>}
        </div>
        <div className="z-row-side">
          <span className="z-ledger-amount">{money(entry.amount, entry.currency)}{L.perRecurrence[entry.recurrence] && <small> {L.perRecurrence[entry.recurrence]}</small>}</span>
          <Button size="sm" tone="ghost" icon={<PencilSimpleIcon />} aria-label={`${L.edit} · ${entry.counterparty}`} title={L.edit} onClick={() => onEdit(entry)} />
          <Button size="sm" tone="ghost" icon={<TrashIcon />} aria-label={`${L.delete} · ${entry.counterparty}`} title={L.delete} onClick={() => onDelete(entry)} />
        </div>
      </div>;
    })}</div>
  </Section>;
}

export function Ledger({ product, adding, onAdding }: { product: C.Product; adding: Direction | null; onAdding: (direction: Direction | null) => void }) {
  const client = useQueryClient();
  const ledger = useQuery(q.ledger(product.id));
  const [editing, setEditing] = useState<C.LedgerEntry | null>(null);
  const [deleting, setDeleting] = useState<C.LedgerEntry | null>(null);
  const remove = useMutation({
    mutationFn: (entry: C.LedgerEntry) => api.del(`/api/products/${encodeURIComponent(product.id)}/ledger/entries/${encodeURIComponent(entry.id)}`),
    onSuccess: async () => { await client.invalidateQueries({ queryKey: ["ledger", product.id] }); notify.success(L.deleted); setDeleting(null); },
  });
  if (ledger.isLoading) return <Skeleton shape="stats" />;
  if (!ledger.data) return <ErrorText error={ledger.error} />;
  const data = ledger.data;
  const form: Editing | null = editing ? { entry: editing, direction: editing.direction } : adding ? { entry: null, direction: adding } : null;
  const close = () => { setEditing(null); onAdding(null); };
  return <div className="z-flow">
    <div className="z-inline z-ledger-head">
      <p className="z-muted">{L.intro} {L.bookkeeping}</p>
      <Button tone="primary" icon={<PlusIcon />} onClick={() => onAdding("cost")}>{L.addCost}</Button>
      <Button tone="soft" icon={<PlusIcon />} onClick={() => onAdding("revenue")}>{L.addRevenue}</Button>
    </div>
    {data.entries.length === 0
      ? <EmptyState title={L.empty}>{L.emptyNote}</EmptyState>
      : <>
        {data.summary.map((summary) => <Summary key={summary.currency} summary={summary} />)}
        <Entries ledger={data} direction="cost" onEdit={setEditing} onDelete={(entry) => { remove.reset(); setDeleting(entry); }} />
        <Entries ledger={data} direction="revenue" onEdit={setEditing} onDelete={(entry) => { remove.reset(); setDeleting(entry); }} />
      </>}
    {form && <Modal open onOpenChange={(open) => { if (!open) close(); }} title={form.entry ? L.edit : form.direction === "cost" ? L.addCost : L.addRevenue}>
      <EntryForm key={form.entry?.id ?? form.direction} product={product} ledger={data} editing={form} onDone={close} />
    </Modal>}
    <Modal open={Boolean(deleting)} onOpenChange={(open) => { if (!open && !remove.isPending) setDeleting(null); }} title={L.delete}
      footer={<><Button onClick={() => setDeleting(null)} disabled={remove.isPending}>{L.cancel}</Button><Button tone="danger" busy={remove.isPending} onClick={() => deleting && remove.mutate(deleting)}>{L.delete}</Button></>}>
      <p>{deleting ? L.deleteConfirm(deleting.counterparty) : ""}</p><ErrorText error={remove.error} />
    </Modal>
  </div>;
}
